/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Button Component For Adding Claims To Manual Worklist
 */

import _ from 'lodash';
import { Button, Modal, List, Typography, Input, Checkbox, Flex, CheckboxChangeEvent, ButtonProps, Tooltip } from 'antd';
import { Info, Plus } from '@icon-park/react';
import { useEffect, useMemo, useState } from 'react';

import { defaultTableSelectionMeta } from 'lib/agGrid/AgGridTable';
import { AssigneesList } from 'components/workLists/response/Widgets';
import useUpdateManualWorklistEntity from 'hooks/claim/mutate/useUpdateManualWorklistEntity';
import useWorkLists from 'hooks/claim/useWorkLists';
import { useAppStoreValues } from 'store/app';
import { getManualWorklistFilterQuery, getTotalSelectionCount, prepareManualWorklistModificationCriteria } from 'utils/claims/workLists';
import { WORKLIST_ENTITY_TYPES } from 'app-constants/claims/workLists';
import { AntClass } from '@veritable/common/constants/antd';

const DEFAULT_SEARCH_PARAMS = {
	criteria: getManualWorklistFilterQuery(),
};

export interface IProps extends ButtonProps {
	claimId?: string;
	label?: string;
	tableSelectionMeta?: ITableSelectionMeta;
	totalCount?: number;
	claimStateGroup?: ClaimRequest.IGroupedClaimWorkState;
	setTableSelectionMeta?: (tableSelectionMeta: ITableSelectionMeta) => void;
	isMultiSelect?: boolean;
	data?: ClaimRequest.IClaim;
}

const AddToManualWorkList: React.FC<IProps> = ({
	claimId,
	tableSelectionMeta,
	label,
	totalCount,
	setTableSelectionMeta,
	claimStateGroup,
	data,
	...props
}) => {
	const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
	const [searchText, setSearchText] = useState<string>('');
	const [selectAll, setSelectAll] = useState<boolean>(false);
	const [selectedItems, setSelectedItems] = useState<string[]>([]);
	const [initialSelectedItems, setInitialSelectedItems] = useState<string[]>([]);

	const { user, userAccess } = useAppStoreValues();
	const { isBillingUser } = userAccess;

	const { data: worklists } = useWorkLists({
		userWorklists: false,
		entityType: WORKLIST_ENTITY_TYPES.claim,
		allWorklists: true,
		searchWorklist: DEFAULT_SEARCH_PARAMS,
	});

	// Billing users see only worklists they are assigned to; others see all
	const availableWorklists = useMemo(() => {
		const list = worklists?.data ?? [];
		if (!isBillingUser || !user?.id) {
			return list;
		}
		return _.filter(list, (item) => _.includes(item?.assignees ?? [], user.id));
	}, [worklists?.data, isBillingUser, user]);

	useEffect(() => {
		const assignedWorklists = data?.metadata?.worklists ?? [];

		if (_.isEmpty(availableWorklists) || _.isEmpty(assignedWorklists)) {
			setSelectedItems([]);
			setSelectAll(false);
			return;
		}

		const worklistIds = _.map(availableWorklists, (item) => item.id);
		const preSelectedMeta = _.filter(assignedWorklists, (item) => _.includes(worklistIds, item?.id));
		const preSelected = _.map(preSelectedMeta, (item) => item?.id);

		setSelectedItems(preSelected);
		setInitialSelectedItems(preSelected);
		setSelectAll(!_.isEmpty(preSelected) && _.size(preSelected) === _.size(worklistIds));
	}, [data?.metadata?.worklists, availableWorklists]);

	const totalSelectionCount = useMemo(() => getTotalSelectionCount(tableSelectionMeta, totalCount), [tableSelectionMeta, totalCount]);

	const hasChanges = useMemo(() => {
		return !_.isEqual(_.sortBy(selectedItems), _.sortBy(initialSelectedItems));
	}, [selectedItems, initialSelectedItems]);

	const { mutate, isPending } = useUpdateManualWorklistEntity();

	const showModal = () => {
		setIsModalVisible(true);
	};

	const resetModal = () => {
		setIsModalVisible(false);
		setSearchText('');
		setSelectedItems([]);
		setSelectAll(false);
	};

	const handleCancel = () => {
		resetModal();
	};

	// get add claims criteria, if only claimId is provided, then add the claimId to the manual worklist,
	// otherwise add the selected items to the manual worklist
	const getAddClaimsCriteria = () => {
		if (claimId) {
			return prepareManualWorklistModificationCriteria({ selectedRows: [claimId], deselectedRows: [], selectAll: false });
		}
		return prepareManualWorklistModificationCriteria(tableSelectionMeta);
	};

	const handleOk = () => {
		mutate(
			{
				entityType: WORKLIST_ENTITY_TYPES.claim,
				multipleWorklistPatch: {
					worklistIds: selectedItems,
					addClaims: {
						conjunction: 'AND',
					},
				},
				claimStateGroup,
				criteria: getAddClaimsCriteria(),
			},
			{
				onSuccess: () => {
					if (_.isFunction(setTableSelectionMeta)) {
						setTableSelectionMeta({
							...defaultTableSelectionMeta,
							criteria: tableSelectionMeta?.criteria,
						});
					}
					resetModal();
				},
			},
		);
	};

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchText(e.target.value);
	};

	const handleSelectAll = (e: CheckboxChangeEvent) => {
		const checked = e.target.checked;
		setSelectAll(checked);
		if (checked) {
			setSelectedItems(_.map(availableWorklists, (item) => item.id) || []);
		} else {
			setSelectedItems([]);
		}
	};

	const handleItemSelect = (id: string, checked: boolean) => {
		if (checked) {
			const newSelectedItems = [...selectedItems, id];
			setSelectedItems(newSelectedItems);
			setSelectAll(_.size(newSelectedItems) === _.size(availableWorklists));
		} else {
			setSelectedItems((prev) => {
				const newSelectedItems = _.filter(prev, (item) => item !== id);
				setSelectAll(false);
				return newSelectedItems;
			});
		}
	};

	const filteredData = _.filter(availableWorklists, (item) => {
		return _.includes(item.name?.toLowerCase(), searchText?.toLowerCase());
	});

	// get dynamic label
	const getLabel = () => {
		if (label) {
			return label;
		} else if (totalSelectionCount && totalSelectionCount > 0) {
			return `Add to Manual Worklist (${totalSelectionCount})`;
		}
		return 'Add to Manual Worklist';
	};

	return (
		<>
			<Button
				type={'primary'}
				className={AntClass.secondaryButton}
				icon={<Plus />}
				onClick={showModal}
				disabled={totalSelectionCount === 0}
				{...props}
			>
				{getLabel()}
			</Button>
			<Modal
				destroyOnHidden
				classNames={{ body: 'h-[500px] overflow-hidden py-4 px-6' }}
				title={
					<Flex align='center' gap={4}>
						<Typography.Title level={4} className='m-0 font-semibold'>
							{'Update Manual Worklist(s)'}
						</Typography.Title>
						<Tooltip title='Select the manual worklists this claim should belong to. You can add or remove it from any list.'>
							<Info fill='#8C8C8C' theme='filled' />
						</Tooltip>
					</Flex>
				}
				open={isModalVisible}
				onCancel={handleCancel}
				width={650}
				footer={[
					<Button key='cancel' onClick={handleCancel}>
						{'Cancel'}
					</Button>,
					<Button key='submit' type='primary' onClick={handleOk} loading={isPending} disabled={!hasChanges}>
						{'Save'}
					</Button>,
				]}
				centered
			>
				<Flex vertical className='h-full w-full'>
					<Flex justify='space-between' align='center' className='sticky top-0 z-1 mb-4'>
						<Input.Search
							className='tw-bordered-search-input mr-4 flex-1'
							placeholder='Search by worklist name'
							value={searchText}
							onChange={handleSearch}
						/>
						<Checkbox checked={selectAll} onChange={handleSelectAll}>
							<Typography.Text>{'Select All'}</Typography.Text>
						</Checkbox>
					</Flex>
					<div className='h-[calc(100%-40px)] overflow-y-auto'>
						<List
							dataSource={filteredData}
							renderItem={(item) => (
								<List.Item className='border-none py-3'>
									<Flex gap={16} align='flex-start' className='w-full'>
										<Checkbox
											checked={_.includes(selectedItems, item.id)}
											onChange={(e) => handleItemSelect(item.id, e.target.checked)}
										/>
										<Flex vertical align='flex-start'>
											<Typography.Text strong>{item.name}</Typography.Text>
											<Flex gap={16} align='center'>
												<Typography.Text>No. of Claims - {item.metadata?.claimsCount || 0}</Typography.Text>
												<AssigneesList
													key={item.id}
													assignedUsers={item.assignees}
													maxLength={3}
													showEmptyTag={false}
												/>
											</Flex>
										</Flex>
									</Flex>
								</List.Item>
							)}
						/>
					</div>
				</Flex>
			</Modal>
		</>
	);
};

export default AddToManualWorkList;
